import { NgTemplateOutlet } from '@angular/common';
import {
  AfterContentInit,
  AfterViewInit,
  Component,
  ContentChildren,
  ElementRef,
  NgZone,
  OnDestroy,
  QueryList,
  TemplateRef,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChildren,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { fromEvent, take } from 'rxjs';
import { AnimatedHeightContainerComponent } from '../animated-height-container/animated-height-container.component';
import { TabComponent } from '../tab/tab.component';
import { TabsSize } from './tabs.enum';
import { TagVariant } from '../tag/tag-variant.enum';
import { TagComponent } from '../tag/tag.component';

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'ct-lib-tabs',
  templateUrl: './tabs.component.html',
  styleUrls: ['./tabs.component.scss'],
  imports: [NgTemplateOutlet, AnimatedHeightContainerComponent, TagComponent],
})
export class TabsComponent
  implements AfterContentInit, AfterViewInit, OnDestroy
{
  public ngZone = inject(NgZone);

  public size = input<TabsSize>(TabsSize.MEDIUM);
  public embedded = input<boolean>(false);
  public animateHeight = input<boolean>(false);
  public autowidth = input<boolean>(false);
  public sticky = input<boolean>(false);
  public rightContainer = input<TemplateRef<HTMLElement> | null>(null);
  public leftContainer = input<TemplateRef<HTMLElement> | null>(null);
  public onTabChange = output<void>();
  public checkDescendants = input<boolean>(true);

  public readonly TabsSize = TabsSize;
  public readonly TagVariant = TagVariant;

  @ContentChildren(TabComponent) directTabs!: QueryList<TabComponent>;
  @ContentChildren(TabComponent, { descendants: true }) nestedTabs!: QueryList<TabComponent>;

  get tabs(): QueryList<TabComponent> {
    return this.checkDescendants() ? this.nestedTabs : this.directTabs;
  }

  @ViewChild('tabsList', { read: ElementRef })
  tabsList!: ElementRef<HTMLUListElement>;
  tabItems = viewChildren<ElementRef<HTMLLIElement>>('tabItem');

  // Convert QueryList to signal
  tabsSignal = signal<TabComponent[]>([]);

  // Indicator position and width
  indicatorLeft = signal<number>(0);
  indicatorWidth = signal<number>(0);

  // Intersection Observer for visibility detection
  private intersectionObserver?: IntersectionObserver;

  selectedTab = computed(() => {
    const tabs = this.tabsSignal();
    return tabs.findIndex((tab) => tab.active());
  });

  constructor() {
    effect(() => {
      const tabs = this.tabsSignal();
      if (tabs.length > 0 && tabs.filter((tab) => tab.active()).length === 0) {
        queueMicrotask(() => this.selectTab(tabs[0]));
      }
    });

    effect(() => {
      const selectedIndex = this.selectedTab();
      if (selectedIndex >= 0) {
        setTimeout(() => this.updateIndicatorPosition(), 0);
      }
    });
  }

  ngAfterContentInit() {
    this.tabs.changes.pipe(untilDestroyed(this)).subscribe(() => {
      this.tabsSignal.set(this.tabs.toArray());
      setTimeout(() => this.updateIndicatorPosition(), 0);
    });
    this.tabsSignal.set(this.tabs.toArray());
    if (this.tabs.filter((tab) => tab.active()).length === 0) {
      queueMicrotask(() => this.selectTab(this.tabs.first));
    }
  }

  ngAfterViewInit() {
    setTimeout(() => this.updateIndicatorPosition(), 0);
    fromEvent(window, 'resize')
      .pipe(untilDestroyed(this))
      .subscribe(() => this.updateIndicatorPosition());

    this.ngZone.onStable.pipe(take(1)).subscribe(() => {
      this.updateIndicatorPosition();
    });
    if (this.tabsList && 'IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.updateIndicatorPosition();
            }
          });
        },
        { threshold: 0.1 }
      );
      this.intersectionObserver.observe(this.tabsList.nativeElement);
    }
  }

  ngOnDestroy() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  selectTab(tab: TabComponent) {
    const tabs = this.tabsSignal();
    tabs
      .filter((t) => t.active())
      .forEach((t) => {
        t.active.set(false);
        t.toggleActiveClass(false);
      });
    tab.active.set(true);
    tab.toggleActiveClass(true);
    this.onTabChange.emit();
    setTimeout(() => this.updateIndicatorPosition(), 0);
    this.scrollToActiveTab(tabs.indexOf(tab));
  }

  private scrollToActiveTab(tabIndex: number) {
    if (
      !this.tabsList ||
      !this.tabItems() ||
      tabIndex < 0 ||
      tabIndex >= this.tabItems().length
    ) {
      return;
    }
    const ulElement = this.tabsList.nativeElement;
    const tabElement = this.tabItems()[tabIndex].nativeElement;
    const currentScrollLeft = ulElement.scrollLeft;
    const ulWidth = ulElement.clientWidth;
    const tabLeft = tabElement.offsetLeft;
    const tabWidth = tabElement.offsetWidth;
    const tabRight = tabLeft + tabWidth;
    const visibleLeft = currentScrollLeft;
    const visibleRight = currentScrollLeft + ulWidth;
    if (tabLeft < visibleLeft) {
      ulElement.scrollTo({
        left: tabLeft,
        behavior: 'smooth',
      });
    } else if (tabRight > visibleRight) {
      ulElement.scrollTo({
        left: tabRight - ulWidth,
        behavior: 'smooth',
      });
    }
  }

  private updateIndicatorPosition() {
    if (!this.tabsList) return;
    const listElement = this.tabsList.nativeElement;
    const listItems = listElement.querySelectorAll('li');
    const selectedIndex = this.selectedTab();
    if (selectedIndex >= 0 && selectedIndex < listItems.length) {
      const activeItem = listItems[selectedIndex] as HTMLElement;
      const left = activeItem.offsetLeft;
      const width = activeItem.offsetWidth;
      this.indicatorLeft.set(left);
      this.indicatorWidth.set(width);
    }
  }
}
